<template>
	<section class="quote-card">
		<QuoteCardTitle class="mb-4">People Also Watch</QuoteCardTitle>

		<div
			v-if="isLoading"
			class="grid lg:grid-cols-4 gap-4"
		>
			<div
				v-for="i in 4"
				:key="i"
				class="animate-pulse bg-slate-300/[0.1] rounded-lg h-[178px]"
			></div>
		</div>

		<div
			v-else-if="stockData && stockData.length > 0"
			class="grid grid-cols-2 lg:grid-cols-4 gap-4"
		>
			<NuxtLink
				v-for="(item, idx) in stockData"
				:key="idx"
				:to="`/quote/${item.SYMBOL}/`"
			>
				<TopGainerBlock
					:key="idx"
					appearance="card"
					:symbol="item.SYMBOL"
					:net_chg="item.NET_CHG"
					:price="item.BID"
					:pc_chg="item.PC_CHG"
					:name="item.NAME"
					:labels="item.labels"
					:prices="item.prices"
				/>
			</NuxtLink>
		</div>

		<div
			v-else-if="error"
			class="text-slate-400 text-center py-4"
		>
			{{ error }}
		</div>

		<div
			v-else
			class="text-slate-400 text-center py-4"
		>
			No suggestions found
		</div>
	</section>
</template>

<script setup lang="ts">
import TopGainerBlock from "@/components/markets/TopGainerBlock.vue";
import { useStocksStore } from "@/stores/useStocksStore";

// Props: expects sector and price to fetch related stocks
const props = defineProps({
	sector: {
		type: String,
		required: false,
		default: null
	},
	price: {
		type: [String, Number],
		required: false,
		default: 0
	}
});

// Access the stocks store
const stocksStore = useStocksStore();

// Types
interface StockData {
	SYMBOL: string;
	NAME: string;
	BID: number;
	NET_CHG: number;
	PC_CHG: number;
	labels: string[];
	prices: number[];
}

// Reactive data
const stockData = ref<StockData[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

// Function to fetch people also watch data
const fetchPeopleAlsoWatch = async () => {
	if (!props.sector) {
		error.value = "No sector provided";
		return;
	}

	isLoading.value = true;
	error.value = null;

	try {
		const response: any = await $fetch("/api/people-also-watch", {
			method: "GET",
			params: {
				sector: props.sector.toLowerCase(),
				price: props.price?.toString() || "0"
			}
		});

		// The API now returns enriched stock data directly
		let finalStockData: StockData[] = [];

		if (response?.related?.length) {
			finalStockData = [...response.related];
		}

		// If we have fewer than 4 stocks, fill with top gainers from store
		if (finalStockData.length < 4) {
			const topGainers = stocksStore.getTopGainerStocks;
			const existingSymbols = new Set(finalStockData.map((stock) => stock.SYMBOL));

			// Get top gainers that aren't already in our list
			const additionalStocks = topGainers
				.filter((stock) => !existingSymbols.has(stock.SYMBOL))
				.slice(0, 4 - finalStockData.length);

			finalStockData = [...finalStockData, ...additionalStocks];
		}

		// Ensure all stocks have prices and labels arrays (default to empty if missing)
		stockData.value = finalStockData.slice(0, 4).map((stock) => ({
			...stock,
			prices: stock.prices || [],
			labels: stock.labels || []
		}));

		// Show warning if data enrichment failed
		if (response?.warning) {
			console.warn("[people-also-watch] Warning:", response.warning);
		}
	} catch (err: any) {
		console.error("[people-also-watch] Error fetching data:", err);
		error.value = "Failed to load related stocks";
		stockData.value = [];
	} finally {
		isLoading.value = false;
	}
};

// Watch for prop changes
watch(
	[() => props.sector, () => props.price],
	() => {
		if (import.meta.client) {
			fetchPeopleAlsoWatch();
		}
	},
	{
		immediate: true
	}
);
</script>
